import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import {
  Button,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  TextField,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { ChevronRight, ChevronLeft } from '@mui/icons-material/'
import getGeocoderResult from 'ui/utils/getGeocoderResult'
import PharmacyInfo from 'ui/partials/PharmacyInfo'
import Stack from 'ui/layout/Stack'
import { Caption, Text1 } from 'ui/shorthands/Texts'
import type {
  PharmacyProps,
  PharmacySearchApiParams,
  LocationParams,
} from 'types/common'
import ModalScrollContent from '../layout/ModalScrollContent'

type SearchMode = 'byName' | 'byInstitution' | 'byAddress'

export type PharmacySearchProps = {
  type: 'med' | 'personal' | 'personal_todokusuri'
  apiKey: string
  personalAddress?: string
  institutionAddress: string
  selected?: PharmacyProps['scuel_id'][]
  onSelect: (pharmacy: PharmacyProps) => void
  onSearch: (
    param: PharmacySearchApiParams
  ) => Promise<PharmacyProps[] | undefined | void>
  // NOTE: 薬局選択のみ
  selectOnly?: boolean
  onCancel?: () => void
  onError: (error: Error) => void
}

const optionsByType: {
  [key in PharmacySearchProps['type']]: {
    value: SearchMode
    label: string
  }[]
} = {
  med: [
    {
      value: 'byName',
      label: i18n.t('薬局名で検索'),
    },
    {
      value: 'byInstitution',
      label: i18n.t('自施設から近い順'),
    },
  ],
  personal: [
    {
      value: 'byName',
      label: i18n.t('薬局名で検索'),
    },
    {
      value: 'byInstitution',
      label: i18n.t('受診先医療機関から近い順'),
    },
    {
      value: 'byAddress',
      label: i18n.t('自宅から近い順'),
    },
  ],
  personal_todokusuri: [
    {
      value: 'byName',
      label: i18n.t('薬局名で検索'),
    },
    {
      value: 'byAddress',
      label: i18n.t('自宅から近い順'),
    },
  ],
}

type SearchSetting = {
  location?: LocationParams
  searchWords?: string
  isPossibleDelivery?: true | undefined
  offset: number
}

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerNoContent: {
    borderBottom: 'none',
  },
  search: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  searchButton: {
    marginLeft: theme.spacing(2),
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1.25, 1),
  },
  footer: {
    width: '100%',
  },
  content: {
    width: '100%',
    flex: 1,
  },
  emptyContent: {
    borderTop: `1px solid ${theme.palette.divider}`,
    height: theme.spacing(18.5),
  },
  pharmacy: {
    position: 'relative',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  filter: {
    padding: theme.spacing(1, 2),
  },
  filterCaption: {
    marginRight: theme.spacing(1),
  },
  noResult: {
    width: '100%',
    paddingTop: theme.spacing(9),
    height: theme.spacing(37),
    textAlign: 'center',
  },
  pagingButton: {
    margin: 0,
    padding: theme.spacing(1),
    minWidth: theme.spacing(3),
  },
}))

const PharmacySearch: React.FC<PharmacySearchProps> = ({
  type,
  apiKey,
  personalAddress,
  institutionAddress,
  selected,
  onSearch,
  onSelect,
  selectOnly,
  onCancel,
  onError,
}) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const searchCount = 30
  const [offset, setOffset] = useState<number>(0)
  const [searchMode, setSearchMode] = useState<SearchMode>('byName')
  const [isPossibleDelivery, setIsPossibleDelivery] = useState<
    true | undefined
  >()
  const [locationState, setLocationState] = useState<{
    [key in SearchMode]?: LocationParams
  }>()
  const [searchWords, setSearchWords] =
    useState<PharmacySearchApiParams['name']>()
  const [pharmacies, setPharmacies] = useState<PharmacyProps[] | undefined>()

  const scrollRef = useRef<HTMLDivElement>(null)

  const getLocation: () => Promise<LocationParams | undefined> = () => {
    return new Promise((resolve) => {
      if (locationState?.[searchMode]) {
        resolve(locationState?.[searchMode])
        return
      }
      let address: string = personalAddress || ''
      if (searchMode === 'byInstitution') {
        address = institutionAddress
      }
      address &&
        getGeocoderResult({
          apiKey,
          address,
          onGetResult: (result) => {
            const locationData = {
              latitude: Number(result.geometry.location.lat()),
              longitude: Number(result.geometry.location.lng()),
            }
            // NOTE: GeocodingAPIの回数を最小にするために取得した座標データをlocationStateに保存
            setLocationState({ ...locationState, [searchMode]: locationData })
            resolve(locationData)
          },
          onError: (error) => {
            onError(error as Error)
            resolve(undefined)
          },
        })
    })
  }

  const searchParams: (setting: SearchSetting) => PharmacySearchApiParams = ({
    location,
    searchWords,
    isPossibleDelivery,
    offset,
  }) => ({
    limit: searchCount + 1, // NOTE: 次のページがあるかどうか調べるため一件多く取得
    offset,
    name: searchWords?.trim(),
    latitude: location?.latitude,
    longitude: location?.longitude,
    is_possible_delivery: isPossibleDelivery,
  })

  const searchHandler = (param: SearchSetting) => {
    setOffset(param.offset || 0)
    setSearchWords(param.searchWords)
    setIsPossibleDelivery(param.isPossibleDelivery)
    onSearch(searchParams(param))
      .then((pharmacies) => {
        pharmacies && setPharmacies(pharmacies)
        scrollRef?.current?.scrollIntoView()
      })
      .catch((error) => console.log(error))
  }

  useEffect(() => {
    // NOTE: タブを切り替えた時に検索を発火
    setPharmacies(undefined)
    switch (searchMode) {
      case 'byName':
        break
      default:
        getLocation().then((location) =>
          searchHandler({
            location,
            offset: 0,
            isPossibleDelivery: undefined,
          })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode])

  useEffect(() => {
    selectOnly &&
      onSearch(searchParams({ offset })).then(
        (data) => data && setPharmacies(data)
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOnly])

  useEffect(() => {
    !!pharmacies &&
      searchHandler({
        location: locationState?.[searchMode],
        searchWords,
        isPossibleDelivery,
        offset: 0,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPossibleDelivery])

  const offsetHandler = (count: number) => {
    const newCount = offset + count
    setOffset(newCount)
    searchHandler({
      location: locationState?.[searchMode],
      searchWords,
      isPossibleDelivery,
      offset: newCount,
    })
  }

  const options = optionsByType[type].filter(
    ({ value }) => !(value === 'byAddress' && !personalAddress)
  )

  const paginationBar =
    pharmacies?.length &&
    pharmacies.length &&
    // NOTE: 1ページ目のみ件数によりバーを非表示
    (offset === 0 ? pharmacies.length > searchCount : true) ? (
      <div className={classes.pagination}>
        <Button
          className={classes.pagingButton}
          color={'primary'}
          onClick={() => offsetHandler(-searchCount)}
          disabled={offset < searchCount}
        >
          <ChevronLeft />
        </Button>
        <Button
          className={classes.pagingButton}
          color={'primary'}
          onClick={() => offsetHandler(searchCount)}
          disabled={searchCount >= pharmacies.length}
        >
          <ChevronRight />
        </Button>
      </div>
    ) : undefined

  const header = (
    <div
      className={cx(
        classes.header,
        // NOTE: スクロールするコンテンツのrootのborderTopが制御できないのでここでborderBottomとして制御
        pharmacies === undefined && classes.headerNoContent
      )}
    >
      <Tabs
        variant="fullWidth"
        value={options.findIndex((o) => o.value === searchMode)}
        indicatorColor="primary"
        textColor="primary"
        onChange={(e, index) => setSearchMode(options[index].value)}
        aria-label="disabled tabs example"
      >
        {options.map((o) => (
          <Tab key={o.label} label={o.label} />
        ))}
      </Tabs>
      {searchMode === 'byName' && (
        <>
          <Stack className={classes.search}>
            <TextField
              value={searchWords}
              onChange={(e) => {
                const words = e.target.value
                  // eslint-disable-next-line no-irregular-whitespace
                  ?.replace(/　/, ' ') // 全角スペースを半角スペースに
                setSearchWords(words || '')
              }}
              placeholder={t('薬局名を入力')}
              variant="outlined"
              fullWidth
            />
            <Button
              className={classes.searchButton}
              variant={'contained'}
              color={'primary'}
              disabled={!searchWords}
              onClick={() => {
                searchHandler({
                  searchWords,
                  isPossibleDelivery,
                  offset: 0,
                })
              }}
            >
              {t('検索')}
            </Button>
          </Stack>
          {type !== 'personal_todokusuri' && (
            <Stack className={classes.filter}>
              <Caption className={classes.filterCaption}>
                {t('絞り込み')}
              </Caption>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPossibleDelivery}
                    onChange={() =>
                      setIsPossibleDelivery(!isPossibleDelivery || undefined)
                    }
                    color="primary"
                  />
                }
                label={
                  type === 'personal'
                    ? t('配送対応')
                    : t('オンライン服薬指導対応')
                }
              />
            </Stack>
          )}
        </>
      )}
      {paginationBar}
    </div>
  )

  return (
    <>
      <ModalScrollContent
        header={
          selectOnly ? (
            <Stack>
              <Text1>
                {t('薬が処方された場合に受け取る薬局を選択してください。')}
              </Text1>
            </Stack>
          ) : (
            header
          )
        }
        footer={
          onCancel && (
            <Stack align={'right'} borderTop className={classes.footer}>
              <Button color="primary" onClick={onCancel}>
                {t('キャンセル')}
              </Button>
            </Stack>
          )
        }
      >
        <div ref={scrollRef} className={classes.content}>
          {pharmacies ? (
            <>
              {pharmacies.length ? (
                pharmacies
                  .filter((p, index) => {
                    return index < searchCount // NOTE: 一件多く取得したのを出さない処理
                  })
                  .map((p: PharmacyProps) => {
                    const isSelected = selected?.includes(p.scuel_id)
                    return (
                      <div key={p.scuel_id} className={classes.pharmacy}>
                        <PharmacyInfo
                          type={type}
                          pharmacy={p}
                          button={
                            <Button
                              color={'primary'}
                              variant={isSelected ? 'text' : 'contained'}
                              onClick={() => onSelect(p)}
                              disabled={isSelected}
                            >
                              {isSelected ? t('設定済') : t('選択')}
                            </Button>
                          }
                        />
                      </div>
                    )
                  })
              ) : (
                <div className={classes.noResult}>
                  <Text1>{t('薬局がありません')}</Text1>
                </div>
              )}
            </>
          ) : (
            // NOTE: searchModeを切り替えた時にtabだけにならないように空欄を追加
            searchMode !== 'byName' && <div className={classes.emptyContent} />
          )}
        </div>
      </ModalScrollContent>
    </>
  )
}

export default PharmacySearch
